import { createTheme, PaletteOptions } from "@mui/material";
import autocompleteClasses from "@mui/material/Autocomplete/autocompleteClasses";
import { CSSProperties } from "react";
import { reds } from "@/colors";
import { grey } from "@mui/material/colors";

declare module "@mui/material/styles" {
  interface PaletteColor {
    shades?: {
      "4p": string;
      "8p": string;
    };
  }

  interface SimplePaletteColorOptions {
    shades?: {
      "4p": string;
      "8p": string;
    };
  }

  interface TypographyVariants {
    labelLarge: CSSProperties;
  }

  interface TypographyVariantsOptions {
    labelLarge?: CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    labelLarge: true;
  }
}

const palette: Partial<PaletteOptions> = {
  primary: {
    main: localStorage.getItem("FITUP:primary.main") ?? "#DE2826",
    dark: localStorage.getItem("FITUP:accent.main") ?? "#A3000C",
    shades: {
      "4p": "rgba(225, 37, 27, 0.04)",
      "8p": "rgba(225, 37, 27, 0.08)",
    },
  },
  secondary: {
    main: localStorage.getItem("FITUP:accent.main") ?? "#B90810",
  },
  text: {
    primary: localStorage.getItem("FITUP:text.primary") ?? "#000000DE",
    secondary: localStorage.getItem("FITUP:text.secondary") ?? "#00000099",
    disabled: localStorage.getItem("FITUP:text.disabled") ?? "#00000061",
  },
  background: {
    default: localStorage.getItem("FITUP:background.default") ?? "#F5F5F5",
    paper: localStorage.getItem("FITUP:background.paper") ?? "#FFFFFF",
  },
  success: {
    main: "#2E7D32",
    dark: "#1B5E20",
    light: "#4CAF50", // colors.green[500]
  },
  error: {
    main: "#C74C3F",
    dark: "#9A352F",
    light: "#B27574",
  },
  info: {
    main: "#0288D1",
    dark: "#01579B",
    light: "#03A9F4", // colors.lightBlue[500]
  },
  warning: {
    main: "#ED6C02",
    dark: "#E65100",
    light: "#FF9800",
  },
  action: {
    active: "rgba(0, 0, 0, 0.28)",
    activatedOpacity: 0.28,
    hoverOpacity: 0.04,
    selectedOpacity: 0.8,
    disabledOpacity: 0.26,
    focusOpacity: 0.12,
  },
};

export const theme = createTheme({
  palette,
  typography: {
    fontFamily: ["Prompt", "sans-serif"].join(","),
    h4: {
      fontSize: "2rem",
      fontWeight: 500,
    },
    labelLarge: {
      fontWeight: 500,
      fontSize: "0.875rem",
    },
    subtitle1: {
      fontWeight: 500,
      size: "1rem",
    },
    subtitle2: {
      fontWeight: 500,
      size: "0.875rem",
    },
    caption: {
      size: "0.75rem",
    },
  },
  shape: {
    borderRadius: 12,
  },
});

theme.components = {
  MuiCssBaseline: {
    styleOverrides: `
      @font-face {
        font-family: 'Prompt';
        font-style: normal;
        font-display: swap;
        font-weight: 300;
        src: local('Prompt'), local('Prompt-Light'), url('/fonts/Prompt-Light.woff2') format('woff2');
      }

      @font-face {
        font-family: 'Prompt';
        font-style: normal;
        font-display: swap;
        font-weight: 400;
        src: local('Prompt'), local('Prompt-Regular'), url('/fonts/Prompt-Regular.woff2') format('woff2');
      }

      @font-face {
        font-family: 'Prompt';
        font-style: normal;
        font-display: swap;
        font-weight: 500;
        src: local('Prompt'), local('Prompt-Medium'), url('/fonts/Prompt-Medium.woff2') format('woff2');
      }
      
      background: ${theme.palette.background.default};

      @keyframes pulse {
        0% {
          transform: scale(1);
        }

        50% {
          transform: scale(1.1);
        }

        100% {
          transform: scale(1);
        }
      }
      
      .SnackbarItem-contentRoot {
        border-radius: ${theme.shape.borderRadius}px !important;
      }
      
      .SnackbarItem-variantSuccess {
        background-color: ${theme.palette.success.main} !important;
      }
      
      .SnackbarItem-variantError {
        background-color: ${theme.palette.error.main} !important;
      }
      
      .SnackbarItem-message > .MuiSvgIcon-root {
        margin-right: ${theme.spacing(2)};
      }
      
      .fc .fc-daygrid-day-top,
      .fc .fc-daygrid-day-number,
      .fc .fc-col-header-cell.fc-day {
        font-size: ${theme.typography.body2.fontSize as string};
        font-weight: ${theme.typography.body2.fontWeight as number};
      }
      
      .fc .fc-col-header-cell .fc-scrollgrid-sync-inner {
        line-height: 2;
      }
      
      .fc .fc-daygrid-day-top {
        flex-direction: row;
      }
      
      .fc .fc-daygrid-day-number {
        padding: ${theme.spacing(1)};
      }
      
      .fc .fc-daygrid-event-dot {
        border-width: 6px;
        border-radius: 12px;
        margin-right: 10px;
      }
      
      .fc .fc-daygrid-dot-event .fc-event-title,
      .fc .fc-v-event .fc-event-title {
        font-size: ${theme.typography.caption.fontSize as string};
        font-weight: ${theme.typography.caption.fontWeight as number};
        line-height: ${theme.typography.caption.lineHeight as number};
      }
      
      .fc .fc-daygrid-day.fc-day-today,
      .fc .fc-timegrid-col.fc-day-today {
        background-color: ${reds["50"]};
      }
      
      .fc .fc-list-table .fc-list-event-time {
        width: 240px;
      }
      
      .fc-list-day .fc-list-day-text,
      .fc-list-day .fc-list-day-side-text {
        font-weight: normal; 
      }
      
      .fc-direction-ltr .fc-list-day-text {
        float: right;
      }
      
      .fc-direction-ltr .fc-list-day-side-text {
        float: left;
      }
      
      .fc .fc-timegrid-slot-minor {
        border-top-style: unset;
      }
      
      .fc .fc-timegrid-event {
        padding: 2px 4px;
        border-radius: ${theme.shape.borderRadius}px;
      }
      
      .fc .fc-list-sticky .fc-list-day.fc-day-today > * {
        background-color: ${reds["50"]};
      }

      .fc .fc-list-sticky .fc-list-day.fc-day-today > * {
        background-color: ${reds["50"]};
      }

      .fc-theme-standard .fc-day-today .fc-list-day-cushion {
        background-color: unset;
      }

      .fc-event {
        cursor: pointer;
      }
      
      .MuiCalendarPicker-root.MuiCalendarPicker-root {
         margin: 0;
      }
      
      .carousel.carousel-slider {
        .dot {
          background-color: ${grey["300"]};
          opacity: 1;
          box-shadow: none ;
        }
        .dot.selected {
          background-color: ${theme.palette.primary.main}
        }
      }
    `,
  },
  MuiButton: {
    defaultProps: {
      size: "large",
    },
    styleOverrides: {
      containedInherit: {
        background: theme.palette.common.white,
        color: theme.palette.common.black,
        "&:hover": {
          background: theme.palette.grey[100],
        },
      },
    },
  },
  MuiCardHeader: {
    defaultProps: {
      titleTypographyProps: {
        variant: "h6",
      },
    },
    styleOverrides: {
      root: {
        padding: theme.spacing(3),
      },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: theme.spacing(3),
      },
    },
  },
  MuiListItem: {
    styleOverrides: {
      root: {
        "&.Mui-selected": {
          background: theme.palette.primary.shades?.["4p"],
          "&:hover": {
            background: theme.palette.action.hover,
          },
          "& > .MuiListItemIcon-root": {
            color: theme.palette.primary.main,
          },
          "& > .MuiListItemText-root": {
            color: theme.palette.text.primary,
          },
        },
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        "&.MuiTableRow-hover:hover": {
          cursor: "pointer",
        },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      head: {
        background: theme.palette.grey[100],
        "&:first-of-type": {
          borderTopLeftRadius: theme.shape.borderRadius,
        },
        "&:last-of-type": {
          borderTopRightRadius: theme.shape.borderRadius,
        },
      },
    },
  },
  MuiPaginationItem: {
    styleOverrides: {
      root: {
        "&.Mui-selected:hover": {
          backgroundColor: theme.palette.primary.dark,
        },
        "&.Mui-selected": {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        },
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: "#0000001F",
        opacity: 0.85,
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        color: theme.palette.action.active,
      },
      sizeMedium: {
        width: 48,
        height: 48,
      },
    },
  },
  MuiLink: {
    styleOverrides: {
      root: {
        textDecorationColor: "inherit",
      },
    },
  },
  MuiDialog: {
    defaultProps: {
      fullWidth: true,
      maxWidth: "lg",
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        ...theme.typography.h6,
        padding: theme.spacing(3),
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        paddingTop: `${theme.spacing(1)} !important`,
        paddingBottom: theme.spacing(2.5),
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: theme.spacing(3),
        "& > :not(:first-of-type)": {
          marginLeft: theme.spacing(2.5),
        },
      },
    },
  },
  MuiSkeleton: {
    styleOverrides: {
      rectangular: {
        borderRadius: "12px",
      },
    },
  },
  MuiBreadcrumbs: {
    styleOverrides: {
      root: {
        "& .MuiLink-root:not(:hover)": {
          textDecoration: "none",
        },
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        "&.Mui-selected": {
          background: theme.palette.primary.shades?.["4p"],

          "&:hover, &:focus": {
            background: theme.palette.primary.shades?.["8p"],
          },
        },
      },
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      listbox: {
        [`& .${autocompleteClasses.option}`]: {
          '&[aria-selected="true"]': {
            backgroundColor: theme.palette.primary.shades?.["4p"],

            [`&.${autocompleteClasses.focused}`]: {
              backgroundColor: theme.palette.primary.shades?.["8p"],
            },
          },
        },
      },
      popupIndicator: {
        width: 24,
        height: 24,
      },
      clearIndicator: {
        width: 24,
        height: 24,
      },
    },
  },
  MuiTypography: {
    defaultProps: {
      variantMapping: { labelLarge: "label" },
    },
  },
  MuiFormControlLabel: {
    styleOverrides: {
      root: {
        marginLeft: 0,
      },
    },
  },
  MuiFormGroup: {
    styleOverrides: {
      root: {
        ".Mui-error .MuiCheckbox-root": {
          color: theme.palette.error.main,
        },
      },
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: {
        "&.Mui-focused": {
          color: theme.palette.text.secondary,
        },
        "&.Mui-error": {
          color: theme.palette.error.main,
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 24,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 24,
      },
    },
  },
};
